import type { Device, Vehicle } from "@fts/core";
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { ConfirmDialog, Dialog, FormError, FormField, fieldAria } from "../components/Dialog";
import { Icon } from "../components/Icon";
import { EmptyState, Mono, Note, PageHeader, Panel } from "../components/ui";
import { DEMO_CLAIM_CODES } from "../data/demo-provider";
import { useI18n, type MessageKey } from "../i18n";
import { formatDate, formatDateTime, relativeTime } from "../lib/format";
import { sitePath } from "../lib/fleet";
import { canManageFleet } from "../lib/permissions";
import { useSession, type TenantData } from "../state/session";
import { canManageOperators } from "../lib/operators";
import { AuthlistBadge } from "./Operators";

/* ---------------- shared bits ---------------- */

/** How often the Devices screen re-reads device state and card-list delivery. */
export const DEVICES_REFRESH_MS = 20_000;

export type DeviceState = "online" | "offline" | "revoked";

export function deviceState(d: Pick<Device, "online" | "revoked">): DeviceState {
  if (d.revoked) return "revoked";
  return d.online ? "online" : "offline";
}

const STATE_TONE: Record<DeviceState, string> = { online: "ok", offline: "neutral", revoked: "crit" };

export function DeviceStatusBadge({ device }: { device: Pick<Device, "online" | "revoked"> }) {
  const { t } = useI18n();
  const s = deviceState(device);
  return (
    <span className={`badge tone-${STATE_TONE[s]}`}>
      <span className={`status-dot dev-${s}`} aria-hidden="true" />
      {t(`deviceState.${s}`)}
    </span>
  );
}

export function LastSeen({ iso }: { iso: string | null }) {
  const { t, locale } = useI18n();
  const rel = relativeTime(iso, locale);
  if (!rel) return <span className="muted">{t("device.neverSeen")}</span>;
  return (
    <time dateTime={iso!} title={formatDateTime(iso, locale) ?? undefined}>
      {rel}
    </time>
  );
}

function vehicleLabel(data: TenantData, v: Vehicle): string {
  const path = sitePath(data.sites, v.siteId).map((s) => s.name);
  return `${v.fleetNo} · ${path[path.length - 1] ?? ""}`;
}

/** Active vehicles without a device (all visible sites are manageable for fleet managers). */
export function pairableVehicles(data: TenantData): Vehicle[] {
  return data.vehicles
    .filter((v) => v.active && !v.deviceId)
    .sort((a, b) => a.fleetNo.localeCompare(b.fleetNo, "tr", { numeric: true }));
}

/** Claimed devices that are not on a vehicle and not revoked. */
export function pairableDevices(data: TenantData): Device[] {
  return data.devices.filter((d) => !d.vehicleId && !d.revoked).sort((a, b) => a.deviceId.localeCompare(b.deviceId));
}

/* ---------------- dialogs ---------------- */

export function ClaimDeviceDialog({ onClose, onClaimed }: { onClose: () => void; onClaimed: (deviceId: string) => void }) {
  const { t } = useI18n();
  const { provider, tenant, refresh } = useSession();
  const [deviceId, setDeviceId] = useState("");
  const [code, setCode] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const idError = touched && !deviceId.trim() ? t("form.required") : null;
  const codeError = touched && code.replace(/[^0-9A-Fa-f]/g, "").length !== 12 ? t("claim.codeFormat") : null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!tenant || !deviceId.trim() || code.replace(/[^0-9A-Fa-f]/g, "").length !== 12) return;
    setBusy(true);
    setError(null);
    try {
      const id = deviceId.trim().toUpperCase();
      await provider.claimDevice(tenant.id, id, code);
      await refresh();
      onClaimed(id);
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  return (
    <Dialog
      title={t("claim.title")}
      description={t("claim.description")}
      onClose={() => !busy && onClose()}
      onSubmit={submit}
      footer={
        <>
          <button type="button" className="button button-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="button button-primary" disabled={busy}>
            {busy ? t("common.working") : t("claim.submit")}
          </button>
        </>
      }
    >
      <FormField id="claim-id" label={t("device.id")} error={idError}>
        <input
          {...fieldAria("claim-id", { error: idError })}
          className="mono input-upper"
          autoComplete="off"
          spellCheck={false}
          placeholder="FTS-000123"
          value={deviceId}
          onChange={(e) => setDeviceId(e.target.value)}
          data-autofocus
        />
      </FormField>
      <FormField id="claim-code" label={t("claim.code")} hint={t("claim.codeHint")} error={codeError}>
        <input
          {...fieldAria("claim-code", { hint: true, error: codeError })}
          className="mono input-upper"
          autoComplete="off"
          spellCheck={false}
          placeholder="XXXX-XXXX-XXXX"
          value={code}
          onChange={(e) => setCode(e.target.value)}
        />
      </FormField>
      {provider.mode === "demo" && (
        <Note>
          {t("claim.demoHint", {
            codes: Object.entries(DEMO_CLAIM_CODES)
              .map(([id, c]) => `${id} → ${c}`)
              .join(", "),
          })}
        </Note>
      )}
      {error !== null && <FormError error={error} />}
    </Dialog>
  );
}

/** Pair a device to a vehicle; pass `device` to choose a vehicle, or `vehicle` to choose a device. */
/** A device on another vehicle needs that vehicle's card list now, not within the minute. */
function usePushLists() {
  const { provider, tenant, role } = useSession();
  return async () => {
    if (tenant && canManageOperators(role)) await provider.syncAuthlists(tenant.id).catch(() => undefined);
  };
}

export function PairDialog({
  data,
  device,
  vehicle,
  onClose,
}: {
  data: TenantData;
  device?: Device;
  vehicle?: Vehicle;
  onClose: () => void;
}) {
  const { t } = useI18n();
  const { provider, refresh } = useSession();
  const pushLists = usePushLists();
  const vehicles = useMemo(() => pairableVehicles(data), [data]);
  const devices = useMemo(() => pairableDevices(data), [data]);
  const [choice, setChoice] = useState("");
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const options = device
    ? vehicles.map((v) => ({ value: v.id, label: vehicleLabel(data, v) }))
    : devices.map((d) => ({ value: d.deviceId, label: d.deviceId }));
  const choiceError = touched && !choice ? t("form.chooseOne") : null;

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!choice) return;
    setBusy(true);
    setError(null);
    try {
      if (device) await provider.pairDevice(device.deviceId, choice);
      else if (vehicle) await provider.pairDevice(choice, vehicle.id);
      await pushLists();
      await refresh();
      onClose();
    } catch (err) {
      setError(err);
      setBusy(false);
    }
  };

  const title = device ? t("pair.titleDevice", { device: device.deviceId }) : t("pair.titleVehicle", { vehicle: vehicle?.fleetNo ?? "" });
  return (
    <Dialog
      title={title}
      description={t("pair.description")}
      onClose={() => !busy && onClose()}
      onSubmit={submit}
      footer={
        <>
          <button type="button" className="button button-secondary" onClick={onClose} disabled={busy}>
            {t("common.cancel")}
          </button>
          <button type="submit" className="button button-primary" disabled={busy || options.length === 0}>
            {busy ? t("common.working") : t("pair.submit")}
          </button>
        </>
      }
    >
      {options.length === 0 ? (
        <Note>{device ? t("pair.noVehicles") : t("pair.noDevices")}</Note>
      ) : (
        <FormField id="pair-choice" label={device ? t("pair.vehicle") : t("pair.device")} error={choiceError} hint={device ? t("pair.vehicleHint") : undefined}>
          <select {...fieldAria("pair-choice", { error: choiceError, hint: !!device })} value={choice} onChange={(e) => setChoice(e.target.value)} data-autofocus>
            <option value="">{t("form.select")}</option>
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </FormField>
      )}
      {error !== null && <FormError error={error} />}
    </Dialog>
  );
}

export function UnpairDialog({ device, vehicle, onClose }: { device: Device; vehicle: Vehicle | undefined; onClose: () => void }) {
  const { t } = useI18n();
  const { provider, refresh } = useSession();
  const pushLists = usePushLists();
  return (
    <ConfirmDialog
      title={t("unpair.title")}
      confirmLabel={t("unpair.submit")}
      danger
      onClose={onClose}
      onConfirm={async () => {
        await provider.unpairDevice(device.deviceId);
        await pushLists();
        await refresh();
        onClose();
      }}
    >
      <p>{t("unpair.body", { device: device.deviceId, vehicle: vehicle?.fleetNo ?? t("common.empty") })}</p>
    </ConfirmDialog>
  );
}

/* ---------------- Devices screen ---------------- */

type Modal = { kind: "claim" } | { kind: "pair"; device: Device } | { kind: "unpair"; device: Device } | null;

function SyncListsButton() {
  const { t } = useI18n();
  const { provider, tenant, refresh } = useSession();
  const [state, setState] = useState<"idle" | "busy" | "done" | "failed">("idle");
  const run = async () => {
    if (!tenant) return;
    setState("busy");
    try {
      await provider.syncAuthlists(tenant.id);
      await refresh();
      setState("done");
    } catch {
      setState("failed");
    }
  };
  return (
    <>
      <button type="button" className="button button-secondary" onClick={() => void run()} disabled={state === "busy"} title={t("authlist.syncHint")}>
        <Icon name="refresh" size={16} />
        {state === "busy" ? t("common.working") : t("authlist.syncNow")}
      </button>
      {state === "done" && <span className="sr-only" role="status">{t("authlist.syncDone")}</span>}
      {state === "failed" && (
        <span className="form-error" role="alert">
          {t("authlist.syncFailed")}
        </span>
      )}
    </>
  );
}

export function Devices({ data }: { data: TenantData }) {
  const { t, locale } = useI18n();
  const { role } = useSession();
  const canManage = canManageFleet(role);
  const canSync = canManageOperators(role);
  const listOf = (id: string) => data.authlist.find((a) => a.deviceId === id);
  // Online state and card-list delivery change on their own (device acks, broker notices): keep them fresh.
  const { refresh } = useSession();
  useEffect(() => {
    const h = setInterval(() => void refresh().catch(() => undefined), DEVICES_REFRESH_MS);
    return () => clearInterval(h);
  }, [refresh]);
  const [modal, setModal] = useState<Modal>(null);
  const [claimed, setClaimed] = useState<string | null>(null);
  const vehicleById = useMemo(() => new Map(data.vehicles.map((v) => [v.id, v])), [data.vehicles]);
  const devices = useMemo(() => [...data.devices].sort((a, b) => a.deviceId.localeCompare(b.deviceId, "en", { numeric: true })), [data.devices]);
  const paired = devices.filter((d) => d.vehicleId).length;
  const dash = t("common.empty");
  const claimedDevice = claimed ? data.devices.find((d) => d.deviceId === claimed) : undefined;

  return (
    <>
      <PageHeader
        title={t("devices.title")}
        subtitle={t("devices.subtitle", { count: devices.length, paired })}
        actions={
          <>
            {canSync && devices.length > 0 && <SyncListsButton />}
            {canManage && (
              <button type="button" className="button button-primary" onClick={() => setModal({ kind: "claim" })}>
                <Icon name="plus" size={16} />
                {t("devices.add")}
              </button>
            )}
          </>
        }
      />

      {claimedDevice && (
        <div className="success-banner" role="status">
          <Icon name="check" size={18} />
          <span>{t("devices.claimed", { device: claimedDevice.deviceId })}</span>
          {!claimedDevice.vehicleId && (
            <button type="button" className="button button-secondary button-sm" onClick={() => setModal({ kind: "pair", device: claimedDevice })}>
              {t("devices.pair")}
            </button>
          )}
          <button type="button" className="icon-button" onClick={() => setClaimed(null)} title={t("common.close")}>
            <Icon name="close" size={16} label={t("common.close")} />
          </button>
        </div>
      )}

      <Panel flush>
        {devices.length === 0 ? (
          <EmptyState icon="chip" title={t("devices.empty")}>
            {canManage ? t("devices.emptyManager") : t("devices.emptyViewer")}
          </EmptyState>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t("device.id")}</th>
                  <th scope="col">{t("device.vehicle")}</th>
                  <th scope="col">{t("device.status")}</th>
                  <th scope="col" className="col-hide-sm">
                    {t("device.lastSeen")}
                  </th>
                  <th scope="col" className="col-hide-sm">
                    {t("authlist.column")}
                  </th>
                  <th scope="col" className="col-hide-md">
                    {t("device.firmware")}
                  </th>
                  <th scope="col" className="col-hide-md">
                    {t("device.claimedAt")}
                  </th>
                  {canManage && (
                    <th scope="col" className="col-actions">
                      <span className="sr-only">{t("common.actions")}</span>
                    </th>
                  )}
                </tr>
              </thead>
              <tbody>
                {devices.map((d) => {
                  const v = d.vehicleId ? vehicleById.get(d.vehicleId) : undefined;
                  return (
                    <tr key={d.deviceId}>
                      <td className="nowrap">
                        <Mono>{d.deviceId}</Mono>
                      </td>
                      <td className="nowrap">
                        {v ? (
                          <a href={`#/vehicles/${encodeURIComponent(v.id)}`} className="row-link mono strong">
                            {v.fleetNo}
                          </a>
                        ) : (
                          <span className="muted">{t("device.unpaired")}</span>
                        )}
                      </td>
                      <td>
                        <DeviceStatusBadge device={d} />
                      </td>
                      <td className="col-hide-sm nowrap">
                        <LastSeen iso={d.lastSeenAt} />
                      </td>
                      <td className="col-hide-sm">{!d.hasCardReader ? <AuthlistBadge status={undefined} noReader /> : d.vehicleId || listOf(d.deviceId) ? <AuthlistBadge status={listOf(d.deviceId)} /> : <span className="muted">{dash}</span>}</td>
                      <td className="col-hide-md mono">{d.fw ?? dash}</td>
                      <td className="col-hide-md nowrap">{formatDate(d.claimedAt, locale) ?? dash}</td>
                      {canManage && (
                        <td className="col-actions">
                          {d.vehicleId ? (
                            <button type="button" className="button button-secondary button-sm" onClick={() => setModal({ kind: "unpair", device: d })}>
                              <Icon name="unlink" size={15} />
                              <span className="button-label">{t("devices.unpair")}</span>
                            </button>
                          ) : !d.revoked ? (
                            <button type="button" className="button button-secondary button-sm" onClick={() => setModal({ kind: "pair", device: d })}>
                              <Icon name="link" size={15} />
                              <span className="button-label">{t("devices.pair")}</span>
                            </button>
                          ) : null}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
      {!canManage && <Note>{t("devices.readOnly")}</Note>}

      {modal?.kind === "claim" && (
        <ClaimDeviceDialog
          onClose={() => setModal(null)}
          onClaimed={(id) => {
            setModal(null);
            setClaimed(id);
          }}
        />
      )}
      {modal?.kind === "pair" && (
        <PairDialog
          data={data}
          device={modal.device}
          onClose={() => {
            setModal(null);
            setClaimed(null);
          }}
        />
      )}
      {modal?.kind === "unpair" && (
        <UnpairDialog device={modal.device} vehicle={modal.device.vehicleId ? vehicleById.get(modal.device.vehicleId) : undefined} onClose={() => setModal(null)} />
      )}
    </>
  );
}

/* ---------------- vehicle detail: device panel ---------------- */

export function VehicleDevicePanel({ data, vehicle }: { data: TenantData; vehicle: Vehicle }) {
  const { t } = useI18n();
  const { role } = useSession();
  const canManage = canManageFleet(role);
  const [modal, setModal] = useState<"pair" | "unpair" | null>(null);
  const device = vehicle.deviceId ? data.devices.find((d) => d.deviceId === vehicle.deviceId) : undefined;
  const dash = t("common.empty");

  const row = (label: MessageKey, value: ReactNode) => (
    <div className="dl-row">
      <dt>{t(label)}</dt>
      <dd>{value}</dd>
    </div>
  );

  return (
    <Panel
      title={t("vehicleDevice.title")}
      meta={
        canManage &&
        (vehicle.deviceId ? (
          <button type="button" className="button button-secondary button-sm" onClick={() => setModal("unpair")} disabled={!device}>
            <Icon name="unlink" size={15} />
            {t("devices.unpair")}
          </button>
        ) : vehicle.active ? (
          <button type="button" className="button button-secondary button-sm" onClick={() => setModal("pair")}>
            <Icon name="link" size={15} />
            {t("vehicleDevice.pair")}
          </button>
        ) : null)
      }
    >
      {vehicle.deviceId ? (
        <dl className="dl">
          {row("device.id", <Mono>{vehicle.deviceId}</Mono>)}
          {device ? (
            <>
              {row("device.status", <DeviceStatusBadge device={device} />)}
              {row("device.lastSeen", <LastSeen iso={device.lastSeenAt} />)}
              {row("authlist.column", <AuthlistBadge status={data.authlist.find((a) => a.deviceId === device.deviceId)} noReader={!device.hasCardReader} />)}
              {row("device.firmware", <span className="mono">{device.fw ?? dash}</span>)}
              {row("device.imei", <span className="mono">{device.imei ?? dash}</span>)}
              {row("device.iccid", <span className="mono">{device.iccid ?? dash}</span>)}
            </>
          ) : null}
        </dl>
      ) : (
        <p className="panel-text muted">{vehicle.active ? t("vehicleDevice.none") : t("vehicleDevice.inactive")}</p>
      )}
      {modal === "pair" && <PairDialog data={data} vehicle={vehicle} onClose={() => setModal(null)} />}
      {modal === "unpair" && device && <UnpairDialog device={device} vehicle={vehicle} onClose={() => setModal(null)} />}
    </Panel>
  );
}
